import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Logo from '../components/logo.jsx';
import { 
    KeyRound, 
    ArrowRight,
    AlertCircle,
    Eye,
    EyeOff,
    Sparkles,
    Zap,
    Shield,
    Mail
} from 'lucide-react';
import { API_BASE_URL } from "../config/apiconfig";

const CreateAccount = () => {
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        confirmPassword: ""
    });
    
    // Removed company logo state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [focusedField, setFocusedField] = useState('');
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({ ...prev, [name]: value }));

        // No recruiter-specific suggestions on signup anymore
        
        // Clear errors when typing
        if (error) setError('');
    };

    // Removed company logo handlers

    // Company suggestions removed from signup. Will be handled in recruiter onboarding.

    const handleCompanySelect = () => {};

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        setSuccess("");

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            setIsLoading(false);
            return;
        }

        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters long");
            setIsLoading(false);
            return;
        }

        // Prepare FormData for submission
        const formDataToSend = new FormData();
        
        // Add all form fields
        formDataToSend.append('email', formData.email);
        formDataToSend.append('password', formData.password);
        // Do not send role here; role will be selected via survey

        try {
            const response = await fetch(`${API_BASE_URL}/api/register`, {
                method: "POST",
                body: formDataToSend, // Send FormData instead of JSON
            });

            const data = await response.json();
            
            if (response.ok) {
                setSuccess("Account created successfully! We've sent a verification email. Please verify your email before logging in.");
                setShowSuccessModal(true);
            } else {
                setError(data.error || "Registration failed");
            }
        } catch (err) {
            setError("Network error. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRoleChange = () => {};

    const renderRoleSpecificFields = () => null;

    // CSS styles as a string
    const styles = `
        @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(3deg); }
        }
        
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes slideInRight {
            from {
                opacity: 0;
                transform: translateX(30px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }
        
        @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
        }

        .background-image {
            background-image: url('https://plus.unsplash.com/premium_photo-1681487837141-7aa3cb70a040?q=80&w=880&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
            background-size: cover;
            background-position: center;
            background-attachment: fixed;
        }

        .blur-overlay {
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.1);
        }

        .glass-card {
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-reverse { animation: float 8s ease-in-out infinite reverse; }
        .animate-float-slow { animation: float 7s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out; }
        .animate-slideInRight { animation: slideInRight 0.6s ease-out; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
    `;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            
            <div className="flex flex-col min-h-screen">
                <Header />
                
                {/* Main Content */}
                <div className="flex-1 background-image relative overflow-hidden">
                    {/* Blur Overlay */}
                    <div className="absolute inset-0 blur-overlay"></div>
                    
                    {/* Enhanced animated background elements */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full bg-gradient-to-r from-blue-400/30 to-blue-600/30 blur-3xl animate-float" />
                        <div className="absolute top-20 right-10 w-60 h-60 rounded-full bg-gradient-to-r from-blue-500/25 to-blue-700/25 blur-3xl animate-float-reverse" />
                        <div className="absolute bottom-20 left-20 w-72 h-72 rounded-full bg-gradient-to-r from-blue-300/20 to-blue-500/20 blur-3xl animate-float-slow" />
                        <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full bg-gradient-to-r from-white/10 to-blue-100/10 blur-2xl animate-pulse" />
                    </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto max-h-[90vh] overflow-y-auto p-6">
                        <h3 className="text-xl font-semibold mb-2">Account Created</h3>
                        <p className="text-gray-600">{success}</p>
                        <p className="text-gray-600 mt-2">If you don't see the email, check your spam folder. You can also request a new link from the login page.</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowSuccessModal(false)} className="px-4 py-2 rounded-lg border">Close</button>
                            <button onClick={() => navigate('/login')} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Go to Login</button>
                        </div>
                    </div>
                </div>
            )}

                    <div className="flex items-stretch min-h-screen relative z-10">
                        {/* Left Hero Section */}
                        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
                            <div className="max-w-lg text-center space-y-8 animate-fadeInUp">
                                {/* Logo only (no circular background) */}
                                <div className="relative">
                                    <div className="relative mx-auto flex items-center justify-center drop-shadow-2xl transform hover:scale-110 transition-all duration-500">
                                        <Logo className="w-16 h-16 text-blue-700" />
                                    </div>
                                </div>

                                {/* Hero text */}
                                <div className="space-y-4">
                                    <h1 className="text-5xl font-black bg-gradient-to-r from-slate-800 via-blue-600 to-blue-800 bg-clip-text text-transparent leading-tight">
                                        Your Career Journey
                                        <br />
                                        <span className="text-4xl">Starts Here</span>
                                    </h1>
                                    <p className="text-lg text-slate-100 leading-relaxed drop-shadow-sm">
                                        Join our community of professionals and recruiters. 
                                        Find your dream job or the perfect candidate with our modern platform.
                                    </p>
                                </div>

                                {/* Features */}
                                <div className="flex flex-wrap gap-4 justify-center">
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Sparkles className="w-4 h-4 text-blue-600 group-hover:animate-spin transition-all" />
                                        <span className="text-sm font-medium text-slate-700">Smart Matching</span>
                                    </div>
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Zap className="w-4 h-4 text-blue-500 group-hover:animate-bounce transition-all" />
                                        <span className="text-sm font-medium text-slate-700">Instant Connect</span>
                                    </div>
                                    <div className="flex items-center gap-2 glass-card px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1">
                                        <Shield className="w-4 h-4 text-blue-700 group-hover:scale-110 transition-transform" />
                                        <span className="text-sm font-medium text-slate-700">Secure Platform</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Form Section */}
                        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                            <div className="w-full max-w-md">
                                {/* Form Container */}
                                <div className="glass-card shadow-2xl rounded-3xl overflow-hidden animate-slideInRight hover:shadow-3xl transition-all duration-500">
                                    <div className="p-10">
                                        {/* Form Header */}
                                        <div className="text-center mb-8 space-y-2">
                                            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-blue-600 bg-clip-text text-transparent">
                                                Create Account
                                            </h2>
                                            <p className="text-slate-600">Join us and start your journey</p>
                                            <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full mx-auto" />
                                        </div>

                                        {/* Role Selection removed: handled in separate survey page */}

                                        <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                                            {/* Common Fields */}

                                            <div className="relative group">
                                                <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 opacity-0 group-hover:opacity-20 blur transition-all duration-300 ${focusedField === 'email' ? 'opacity-30' : ''}`} />
                                                <div className="relative">
                                                    <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-all duration-300 ${focusedField === 'email' ? 'text-blue-600' : 'text-slate-400'}`} />
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="Email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocusedField('email')}
                                                        onBlur={() => setFocusedField('')}
                                                        required
                                                        disabled={isLoading}
                                                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:border-blue-500 focus:bg-white focus:shadow-lg focus:outline-none transition-all duration-300 disabled:bg-slate-50 disabled:cursor-not-allowed placeholder:text-slate-400 hover:border-slate-300"
                                                    />
                                                </div>
                                            </div>

                                            {/* Role-specific fields removed */}

                                            {/* Password Fields */}
                                            <div className="relative group">
                                                <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 opacity-0 group-hover:opacity-20 blur transition-all duration-300 ${focusedField === 'password' ? 'opacity-30' : ''}`} />
                                                <div className="relative">
                                                    <KeyRound className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-all duration-300 ${focusedField === 'password' ? 'text-blue-600' : 'text-slate-400'}`} />
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        name="password"
                                                        placeholder="Password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocusedField('password')}
                                                        onBlur={() => setFocusedField('')}
                                                        required
                                                        disabled={isLoading}
                                                        className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:border-blue-500 focus:bg-white focus:shadow-lg focus:outline-none transition-all duration-300 disabled:bg-slate-50 disabled:cursor-not-allowed placeholder:text-slate-400 hover:border-slate-300"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                                                        tabIndex={-1}
                                                    >
                                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="relative group">
                                                <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 opacity-0 group-hover:opacity-20 blur transition-all duration-300 ${focusedField === 'confirmPassword' ? 'opacity-30' : ''}`} />
                                                <div className="relative">
                                                    <KeyRound className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-all duration-300 ${focusedField === 'confirmPassword' ? 'text-blue-600' : 'text-slate-400'}`} />
                                                    <input
                                                        type={showConfirmPassword ? 'text' : 'password'}
                                                        name="confirmPassword"
                                                        placeholder="Confirm Password"
                                                        value={formData.confirmPassword}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocusedField('confirmPassword')}
                                                        onBlur={() => setFocusedField('')}
                                                        required
                                                        disabled={isLoading}
                                                        className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:border-blue-500 focus:bg-white focus:shadow-lg focus:outline-none transition-all duration-300 disabled:bg-slate-50 disabled:cursor-not-allowed placeholder:text-slate-400 hover:border-slate-300"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                                                        tabIndex={-1}
                                                    >
                                                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Error Message */}
                                            {error && (
                                                <div className="flex items-center gap-3 text-red-700 bg-red-50/90 backdrop-blur-sm border border-red-200 p-4 rounded-xl animate-shake">
                                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                                    <span className="text-sm font-medium">{error}</span>
                                                </div>
                                            )}

                                            {/* Success Message */}
                                            {success && !showSuccessModal && (
                                                <div className="flex items-center gap-3 text-green-700 bg-green-50/90 backdrop-blur-sm border border-green-200 p-4 rounded-xl">
                                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                                    <span className="text-sm font-medium">{success}</span>
                                                </div>
                                            )}

                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full relative group overflow-hidden py-4 px-6 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-800 transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:from-blue-500 disabled:hover:to-blue-700"
                                            >
                                                <div className="relative flex items-center justify-center">
                                                    {isLoading ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                                                            <span>Creating Account...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>Create Account</span>
                                                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                                        </>
                                                    )}
                                                </div>
                                            </button>
                                        </form>

                                        {/* Login Link */}
                                        <div className="text-center mt-6 pt-6 border-t border-slate-200">
                                            <p className="text-slate-600">
                                                Already have an account?{' '}
                                                <button
                                                    onClick={() => navigate("/login")}
                                                    className="font-semibold bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent hover:from-blue-700 hover:to-blue-500 transition-all duration-300 hover:underline"
                                                    disabled={isLoading}
                                                >
                                                    Sign In
                                                </button>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <Footer />
            </div>
        </>
    );
};

export default CreateAccount;